import { useAppTheme } from "../../../hooks/useAppTheme";
import React, { useEffect, useRef, useState } from "react";
import { View, Text } from "../../../components/shared/Themed";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { Dimensions, TouchableOpacity, StyleSheet } from "react-native";
import type { ThemeColors } from "../../../utils/constants/colors";
import { AntDesign } from "@expo/vector-icons";
import { ScrollView } from "react-native-gesture-handler";
import { headersCustomerStatus } from "../utils/customerStatusHeaders";
import { useSelector } from "react-redux";
import { translateFields, useTranslation } from "../../../utils/useTranslation";
import { customerStatusTranslationMap } from "../../../utils/translations";

interface OrderItem {
  AccountStatus?: string;
  CardCode?: string;
  CardName?: string;
  PymntGroup?: string;
  CreditLine?: string;
  CreditLimitAvailable?: string;
  Variance?: string;
  Balance?: string;
  OpenDeliveries?: string;
  CommittedOrders?: string;
  image?: string;
}

interface RouteParams {
  order: OrderItem;
}

interface DetailItem {
  label: string;
  value: string;
  supportingValue?: string;
}

const CustomerStatusDetailReportScreen = () => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const { order } = route.params || { order: {} };
  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width
  );
  const navigation = useNavigation();
  const { t } = useTranslation();
  const { language } = useSelector((state: any) => state.configurations);

  // Determine if we're on a small screen (mobile)
  const isMobileView = windowWidth < 768;

  useEffect(() => {
    // Redirect if order is invalid
    if (!order || typeof order === "string") {
      navigation.navigate("CustomerStatus");
      return;
    }

    // Track screen size changes
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setWindowWidth(window.width);
    });

    // Cleanup on unmount
    return () => subscription?.remove();
  }, [order, navigation]);

  const DrawerHeader = () => {
    return (
      <View style={styles.drawerHeaderStyle}>
        <View style={styles.headerRow}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => {
              navigation.navigate("CustomerStatus");
            }}
          >
            <AntDesign name="arrowleft" size={24} color={colors.text} />
          </TouchableOpacity>

          <View style={styles.headerTitleContainer}>
            <Text selectable={true} style={styles.headerTitle}>
              {`${t("customerCode")}: ${order?.CardCode || ""}`}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const ProductDetails = () => {
    const excludedKeys = [""];

    const orderedKeys = [
      "AccountStatus",
      "CardCode",
      "CardName",
      "PymntGroup",
      "CreditLine",
      "CreditLimitAvailable",
      "Variance",
      "Balance",
      "OpenDeliveries",
      "CommittedOrders",
      "SlpName",
    ];

    const detailItems: DetailItem[] = orderedKeys
      .filter((key) => !excludedKeys.includes(key))
      .map((key) => {
        return {
          label: translateFields(key, t, customerStatusTranslationMap) || key,
          value: order?.[key as keyof OrderItem]?.toLocaleString() || "-",
        };
      });
    return (
      <View style={styles.productDetailsContainer}>
        <View style={styles.header}>
          <Text selectable={true} style={styles.title}>{order?.CardCode || ""}</Text>
          <Text selectable={true} style={styles.subtitle}>{order?.CardName || ""}</Text>
        </View>
        <View style={styles.body}>
          {detailItems.map((item, index) => (
            <View key={index} style={styles.detailRow}>
              <Text selectable={true} style={styles.label}>{item.label}:</Text>
              <Text selectable={true} style={styles.value}>{item.value?.toLocaleString()}</Text>
              {item.supportingValue && (
                <Text selectable={true} style={styles.supporting}>({item.supportingValue})</Text>
              )}
            </View>
          ))}
        </View>
      </View>
    );
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <DrawerHeader />
      <View
        style={[
          styles.contentContainer,
          isMobileView && styles.contentContainerMobile,
        ]}
      >
        <ProductDetails />
      </View>
    </ScrollView>
  );
};

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    container: {
      flexGrow: 1,
      backgroundColor: colors.background,
    },
    contentContainer: {
      padding: 16,
      flexDirection: "row",
      justifyContent: "space-between",
    },
    contentContainerMobile: {
      flexDirection: "column",
    },
    orderDetails: {
      flex: 1,
      maxWidth: "48%",
    },
    orderDetailsMobile: {
      maxWidth: "100%",
      marginBottom: 16,
    },
    imageSection: {
      flex: 1,
      maxWidth: "48%",
    },
    imageSectionMobile: {
      maxWidth: "100%",
      marginTop: 20,
    },
    detailBox: {
      borderRadius: 14,
      padding: 16,
      minHeight: "100%",
    },
    detailRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    verticalLine: {
      width: 1,
      backgroundColor: colors.border,
      marginHorizontal: 16,
    },
    drawerHeaderStyle: {
      width: "100%",
      zIndex: 999,
      backgroundColor: colors.surface,
      height: 56,
      borderBottomWidth: 1,
      borderColor: colors.border,
      justifyContent: "center",
      paddingHorizontal: 16,
    },
    headerRow: {
      flexDirection: "row",
      alignItems: "center",
    },
    backButton: {
      paddingVertical: 8,
      paddingRight: 16,
    },
    headerTitleContainer: {
      flex: 1,
    },
    headerTitle: {
      fontWeight: "700",
      fontSize: 16,
    },
    // Product Details Styles
    productDetailsContainer: {
      flex: 1,
    },
    header: {
      marginBottom: 16,
    },
    title: {
      fontSize: 20,
      fontWeight: "bold",
      marginBottom: 4,
      color: colors.text,
    },
    subtitle: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 16,
    },
    body: {
      flex: 1,
    },
    label: {
      fontWeight: "600",
      fontSize: 14,
      flex: 1,
      color: colors.text,
    },
    value: {
      fontSize: 14,
      color: colors.text,
      flex: 1,
      textAlign: "right",
    },
    supporting: {
      fontSize: 12,
      color: colors.textMuted,
      marginLeft: 8,
    },
    // Image Styles
    imageContainer: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      minHeight: 250,
      backgroundColor: colors.surface,
      borderRadius: 8,
    },
    productImage: {
      width: "100%",
      minHeight: "100%",
      flex: 1,
      minHeight: 250,
    },
    loader: {
      position: "absolute",
      zIndex: 1,
    },
  });
}

export default CustomerStatusDetailReportScreen;
